import { useCallback, useEffect, useState } from 'react'
import {
    getAllReleaseNotes,
    getReleaseNotesState,
    markAllNotesAsRead,
    markNoteAsRead,
    RELEASE_NOTES_CHANGED,
} from '@/services/releaseNotes'
import type { ReleaseNote } from '@/types/releaseNotes'

// Same cadence the sidebar card used to re-read the unread count.
const REFRESH_MS = 5 * 60 * 1000

/**
 * Release notes plus the read state kept in localStorage. The topbar button and
 * the side panel each call this and stay in step through RELEASE_NOTES_CHANGED
 * (same tab) and the `storage` event (other tabs).
 */
export function useReleaseNotes() {
    const [notes] = useState<ReleaseNote[]>(() => getAllReleaseNotes())
    const [readIds, setReadIds] = useState<string[]>(
        () => getReleaseNotesState().readNotes
    )

    useEffect(() => {
        const refresh = () => setReadIds(getReleaseNotesState().readNotes)
        window.addEventListener(RELEASE_NOTES_CHANGED, refresh)
        window.addEventListener('storage', refresh)
        const interval = setInterval(refresh, REFRESH_MS)
        return () => {
            window.removeEventListener(RELEASE_NOTES_CHANGED, refresh)
            window.removeEventListener('storage', refresh)
            clearInterval(interval)
        }
    }, [])

    const markRead = useCallback((id: string) => markNoteAsRead(id), [])
    const markAllRead = useCallback(() => markAllNotesAsRead(), [])
    const unreadCount = notes.filter((n) => !readIds.includes(n.id)).length

    return { notes, readIds, unreadCount, markRead, markAllRead }
}
